import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Bell, FileSearch, Plug } from 'lucide-react';
import { PageIdentity } from '../../shell/PageIdentity.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

// REQ-FE-029j — `/admin/settings` placeholder route. The mockup
// (`10-admin-settings.png`) positions three concerns under
// `ADMINISTRATION · GLOBAL`: Iopole connection, notifications, and the
// audit log. All three are "Bientôt disponible" today; the audit card
// is the only one that already links somewhere real (`/admin/audit`).
//
// Footer caveat mirrors the mockup: "Édition disponible avec le
// sous-projet S0 (tenancy + SSO)."

interface AdminCardProps {
  // REQ-FE-030: icon accepts a Lucide React element (or any ReactNode).
  icon: ReactNode;
  title: string;
  description: string;
  testId: string;
  // When set, the card becomes a Link to this destination. Otherwise
  // it renders as an inert article and shows "Bientôt disponible".
  to?: string;
  status?: string;
}

function AdminCard({ icon, title, description, testId, to, status }: AdminCardProps) {
  const body = (
    <>
      <div className="admin-settings__card-head">
        <span className="admin-settings__card-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="admin-settings__card-title">{title}</span>
      </div>
      <p className="muted admin-settings__card-desc">{description}</p>
      <div className="muted admin-settings__card-status">{status ?? 'Bientôt disponible.'}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="admin-settings__card" data-testid={testId}>
        {body}
      </Link>
    );
  }
  return (
    <article className="admin-settings__card admin-settings__card--inert" data-testid={testId}>
      {body}
    </article>
  );
}

export function AdminSettingsScreen() {
  return (
    <PageWrapper size="narrow">
      <section className="admin-settings" aria-label="Paramètres de l'administration">
        <PageIdentity
          testId="admin-settings"
          eyebrow="ADMINISTRATION · GLOBAL"
          title="Paramètres"
          subtitle="Configuration partagée par les six sociétés du groupe. Connexion PDP, notifications, journal d'audit."
        />

        <div
          className="admin-settings__grid"
          data-testid="admin-settings-grid"
          aria-label="Cartes de configuration globale"
        >
          <AdminCard
            icon={<Plug aria-hidden="true" size={16} strokeWidth={1.5} />}
            title="Connexion Iopole"
            description="Identifiants OAuth2 + customer-id + webhooks. Géré via 1Password (vault altyor-invoicing) en attendant l'UI de gestion."
            testId="admin-settings-card-iopole"
          />
          <AdminCard
            icon={<Bell aria-hidden="true" size={16} strokeWidth={1.5} />}
            title="Notifications"
            description="Règles d'alerte par rôle et par société : urgences, retards, litiges, validations en attente."
            testId="admin-settings-card-notifications"
          />
          <AdminCard
            icon={<FileSearch aria-hidden="true" size={16} strokeWidth={1.5} />}
            title="Journal d'audit"
            description="Historique des actions opérateur. Export CSV. Conservation WORM 10 ans."
            testId="admin-settings-card-audit"
            to="/admin/audit"
            status="Accéder au journal →"
          />
        </div>

        <p className="muted admin-settings__caveat" data-testid="admin-settings-caveat">
          Édition disponible avec le sous-projet S0 (tenancy + SSO).
        </p>
      </section>
    </PageWrapper>
  );
}
